import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { primaryRole } from '@/features/common/labels';
import { ProfileMenu } from '@/features/common/layout/ProfileMenu';
import { PublicNavGroup } from '@/features/common/layout/PublicNavGroup';
import { useInformationLinks } from '@/features/common/layout/useInformationLinks';
import { useSession } from '@/app/session';
import { roleLabel } from '@/i18n/roleLabels';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import { ADMIN_LOGIN_PATH } from '@/services/adminSession';
import { currentRealm, REALM } from '@/services/realm';
import '@/features/common/layout/site-header.css';
import '@/features/common/layout/site-nav.css';

/**
 * The six primary destinations, in the order the design fixes them. The rest of what tor.md §6.1
 * asks the menu to carry sits behind the „Informații” group after them (useInformationLinks).
 */
const NAV: [string, keyof typeof publicLayout.ro][] = [
  ['/', 'navHome'],
  ['/catalog', 'navCatalog'],
  ['/consilieri', 'navAdvisorsShort'],
  ['/instruiri', 'navTrainingsShort'],
  ['/evenimente', 'navEventsShort'],
  ['/stiri', 'navNewsShort'],
];

/**
 * The public site header: brand, primary navigation, search, and either the M-Pass entry point
 * or the signed-in avatar.
 *
 * Replaces `layout/header.html`'s `header(activeView)` fragment. Two things it does NOT carry
 * across, both deliberately:
 *
 * - The `activeView` parameter. The template had to be told which nav item was current because
 *   a server-rendered page has no idea where it is; NavLink knows, and `aria-current="page"`
 *   now follows the router rather than a string the controller had to remember to pass.
 * - `base.html`'s speculation-rules prefetch. It existed to make server round-trips feel less
 *   slow, and carried a load-bearing exclusion so hovering a `?lang=` link would not silently
 *   switch the visitor's language before they clicked. Client-side routing removes the round
 *   trip and the language links along with it, so the whole apparatus goes.
 */
export function PublicHeader() {
  const { t } = useTranslation(publicLayout);
  const adminRealm = currentRealm() === REALM.admin;
  const { language } = useLanguage();
  const { user } = useSession();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const informationLinks = useInformationLinks();

  // A route change closes the drawer. Without this, tapping a link on a phone navigates behind
  // an open overlay and the visitor has to dismiss it to see where they landed.
  useEffect(() => setNavOpen(false), [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!navRef.current?.contains(target) && !toggleRef.current?.contains(target)) {
        setNavOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [navOpen]);

  // The search page carries its own larger copy of this control under its <h1>; showing both
  // left the same query sitting in two boxes at once.
  const onSearchPage = location.pathname === '/cautare';

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <Link className="brand" to="/" aria-label={t('brandAria')}>
          <span className="brand__mark" aria-hidden="true">
            <span className="brand__mark-text">AK</span>
          </span>
          <span className="brand__word" aria-hidden="true">
            AKIS
          </span>
        </Link>

        <nav
          ref={navRef}
          // 'is-open', not 'open': the stylesheet keys the mobile drop-down off .site-nav.is-open
          // and public-site-responsive.spec.ts asserts on that exact name.
          className={'site-nav' + (navOpen ? ' is-open' : '')}
          id="site-nav"
          aria-label={t('mainNavAria')}
        >
          {NAV.map(([to, key]) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {t(key)}
            </NavLink>
          ))}
          <PublicNavGroup label={t('navInformation')} links={informationLinks} />
        </nav>

        <div className="header-actions">
          {/* One search control at every width, as MUD has it: Components/header.html gives both
           * header--desktop (line 543) and header__mobile (733) a header__toggle button, and puts
           * the field itself inside the modal it opens — never in the header row. This header
           * used to carry an inline .search-input on desktop and hide it below 991px, standing a
           * "Caută" text link inside the nav panel in its place: three treatments of one feature,
           * and on a phone it was two taps deep and invisible until the menu was opened.
           *
           * Where this differs from MUD: the trigger goes to /cautare instead of opening a modal.
           * That page is the same field and already exists; a modal would be the fifth one-off
           * overlay here (news-modal, side-drawer, drawer-overlay, confirm-dialog) — worth
           * building once as a shared component, not for this alone.
           *
           * Search sits before the sign-in button because that is MUD's own order, not in spite
           * of it. Components/header.html builds the desktop row as header__utilities (with
           * header__search inside it, line 432) and only then header__cta with "Intră în cabinet"
           * and "Obține semnătura" (584) — search first, the calls to action after it. The Header
           * page in Figma shows the same row in all three variations. The mobile export at line
           * 731 happens to place the sign-in link ahead of the search toggle; the desktop order is
           * the one followed here, at every width, so the control does not move as the window
           * narrows. */}
          {!onSearchPage && (
            <Link className="header-search-toggle" to="/cautare" aria-label={t('navSearchButton')}>
              <Icon name="search" className="header-search-toggle__glyph" />
            </Link>
          )}

          {user ? (
            <ProfileMenu
              fullName={user.fullName}
              roleLabel={user.roles?.length ? roleLabel(primaryRole(user.roles), language) : null}
              profileHref="/app/cabinet"
            />
          ) : // A real navigation, not a router link: /saml/authenticate/mpass is a server
          // redirect chain out to the identity provider. On the administration's host the
          // door is its own password sign-in instead (ADM-1).
          adminRealm ? (
            <a className="btn btn--primary btn--md" href={ADMIN_LOGIN_PATH}>
              <span className="btn__label-full">{t('navAdminLogin')}</span>
              <span className="btn__label-short">{t('navAdminLoginShort')}</span>
            </a>
          ) : (
            <a className="btn btn--primary btn--md" href="/saml/authenticate/mpass">
              <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
              <span className="btn__label-full">{t('navMpass')}</span>
              <span className="btn__label-short">{t('navMpassShort')}</span>
            </a>
          )}

          <button
            ref={toggleRef}
            type="button"
            // The same flag drives the bars folding into an X (.nav-toggle.is-open .nav-toggle__bar).
            className={'nav-toggle' + (navOpen ? ' is-open' : '')}
            aria-expanded={navOpen}
            aria-controls="site-nav"
            aria-label={navOpen ? t('closeMenuAria') : t('openMenuAria')}
            onClick={() => setNavOpen((v) => !v)}
          >
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
          </button>
        </div>
      </div>
    </header>
  );
}
